import React, { useState, useEffect } from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle2, XCircle, X, RefreshCw, Award, ArrowRight } from 'lucide-react';

interface ComplianceAuditModalProps {
  isOpen?: boolean;
  onClose: () => void;
  docType: string;
  sectionData: any;
  clientName: string;
  onApplyCorrections?: (corrections: any) => void;
}

export const ComplianceAuditModal: React.FC<ComplianceAuditModalProps> = ({
  isOpen = true,
  onClose,
  docType,
  sectionData,
  clientName,
  onApplyCorrections
}) => {
  const [loading, setLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/clinical/docs/ai-missing-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          docType,
          sectionData,
          clientName
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Audit request failed');
      }

      const data = await res.json();
      setAuditResult(data.review || data);
    } catch (err: any) {
      setError(err.message || 'Failed to complete compliance audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      runAudit();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#f8fafc] border border-slate-200 rounded-2xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden text-slate-800 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-700/80 flex items-center justify-between bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727]">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-lg bg-white/10 border border-white/20 text-slate-100">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white flex items-center space-x-2">
                <span>Clinical Documentation Review</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/10 text-slate-100 border border-white/20 uppercase">
                  {docType} Standards
                </span>
              </h2>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Documentation completeness review for <strong className="text-white">{clientName}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto text-xs flex-1">
          {loading ? (
            <div className="py-12 text-center space-y-3">
              <RefreshCw className="w-6 h-6 animate-spin text-sky-700 mx-auto" />
              <p className="text-slate-900 font-semibold text-xs">
                Reviewing document against Arizona DES/DDD & Medicaid documentation standards...
              </p>
              <p className="text-slate-600 text-[11px]">
                Checking medical necessity, baseline metrics, attendance accounting, and home program oversight.
              </p>
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 space-y-2">
              <div className="font-bold flex items-center space-x-1.5">
                <AlertTriangle className="w-4 h-4" />
                <span>Review Request Error</span>
              </div>
              <p>{error}</p>
              <button
                onClick={runAudit}
                className="px-3 py-1 bg-rose-600 hover:bg-rose-500 text-white rounded font-semibold text-[11px]"
              >
                Retry Review
              </button>
            </div>
          ) : auditResult ? (
            <>
              {/* Scorecard Strip */}
              {(() => {
                const isPass = auditResult.isComplete ?? auditResult.pass ?? true;
                const totalItems = auditResult.checklist?.length || 1;
                const completedItems = auditResult.checklist?.filter((c: any) => c.status === 'COMPLETE' || c.status === 'MET' || c.met === true).length || 0;
                const score = auditResult.score || `${Math.round((completedItems / totalItems) * 100)}%`;

                return (
                  <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-xs flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                          isPass
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                            : 'bg-amber-50 border border-amber-200 text-amber-800'
                        }`}
                      >
                        {score}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-900 flex items-center space-x-1.5">
                          {isPass ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-700" />
                          )}
                          <span>
                            {isPass
                              ? 'Meets Core Documentation & Compliance Standards'
                              : 'Assistive Review: Recommended Items to Verify'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-600 mt-0.5">
                          {auditResult.summary || (isPass
                            ? 'All required documentation elements are present. Ready for clinician and guardian signatures.'
                            : 'Review the flagged items below as guidance to ensure complete records prior to signing.')}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={runAudit}
                      className="p-2 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Re-run review"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                );
              })()}

              {/* Checklist Items */}
              {Array.isArray(auditResult.checklist) && auditResult.checklist.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                      Assistive Documentation Checklist
                    </span>
                    <span className="text-[10px] text-slate-500">
                      Non-blocking clinical guidance
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {auditResult.checklist.map((item: any, idx: number) => {
                      const isComplete = item.status === 'COMPLETE' || item.status === 'MET' || item.met === true || item.passed === true;
                      const isAttention = item.status === 'ATTENTION' || item.status === 'RECOMMENDED';
                      const tipText = item.tip || item.notes;

                      return (
                        <div
                          key={idx}
                          className={`p-2.5 rounded-lg border flex items-start justify-between gap-3 ${
                            isComplete
                              ? 'bg-emerald-50 border-emerald-200 text-slate-800'
                              : isAttention
                              ? 'bg-sky-50 border-sky-200 text-slate-800'
                              : 'bg-amber-50 border-amber-200 text-slate-800'
                          }`}
                        >
                          <div className="flex items-start space-x-2">
                            {isComplete ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                            ) : isAttention ? (
                              <ShieldCheck className="w-4 h-4 text-sky-700 shrink-0 mt-0.5" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                            )}
                            <div>
                              <span className="font-semibold text-xs text-slate-900">
                                {item.requirement || item.rule || item.item}
                              </span>
                              {tipText && (
                                <p className="text-[11px] text-slate-600 mt-0.5">{tipText}</p>
                              )}
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase shrink-0 ${
                              isComplete
                                ? 'bg-emerald-100 text-emerald-800'
                                : isAttention
                                ? 'bg-sky-100 text-sky-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {isComplete ? 'COMPLETE' : isAttention ? 'ADVISORY' : 'ACTION ITEM'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Missing Items List */}
              {Array.isArray(auditResult.missingItems) && auditResult.missingItems.length > 0 && (
                <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
                  <div className="text-[11px] font-bold text-amber-800 flex items-center space-x-1.5 uppercase tracking-wider">
                    <XCircle className="w-4 h-4 text-amber-700" />
                    <span>Missing or Incomplete Items</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 text-[11px]">
                    {auditResult.missingItems.map((m: string, idx: number) => (
                      <li key={idx} className="leading-relaxed">
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actionable Recommendations */}
              {Array.isArray(auditResult.recommendations) && auditResult.recommendations.length > 0 && (
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 shadow-xs space-y-2">
                  <div className="text-[11px] font-bold text-sky-800 uppercase tracking-wider flex items-center space-x-1.5">
                    <Award className="w-3.5 h-3.5" />
                    <span>Clinical Best Practice Recommendations</span>
                  </div>
                  <ul className="space-y-1.5 text-slate-700 text-[11px]">
                    {auditResult.recommendations.map((rec: string, idx: number) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <ArrowRight className="w-3.5 h-3.5 text-sky-700 shrink-0 mt-0.5" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-slate-200 bg-white flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs transition-colors shadow"
          >
            Done Reviewing
          </button>
        </div>
      </div>
    </div>
  );
};
